import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable, type DataTableRowClickEvent} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {Tooltip} from "primereact/tooltip";
import {type RefObject, useMemo} from "react";

import {lifeCycleStateLabelKey, type RevaLifeCycleState} from "@reva/shared/lifecycle-state";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {clearDuplicateValuations, selectDuplicateValuationsData} from "../../redux/addressSlice";
import {fetchPersistence, initializePersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {selectDarkTheme} from "../../redux/themeSlice";
import {getLifeCycleStateColors} from "../../util/chartUtils";
import {formatDateAndTime} from "../../util/dateUtil";
import {ColorDotLabel} from "./startPageWidgets";

interface DuplicateValuationDialogProps {
    addressInputRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * One flat table row: plain-string fields so every column is sortable via `field`, plus the raw
 * `lifeCycleState`/`owner` the body templates need (state color dot, lock tooltip). One representation.
 */
interface DuplicateRow {
    revaId: string;
    /** false = the backend says the caller may not open this valuation (row greyed, not clickable). */
    openable: boolean;
    /** Raw life-cycle state for the color dot (the localized label lives in `stateText`). */
    lifeCycleState: RevaLifeCycleState | null;
    /** Owner (full name, else username) for the lock tooltip on non-openable rows. */
    owner: string | null;
    segmentText: string;
    stateText: string;
    editorsText: string;
    ordersText: string;
    creationDate: string;
    apartmentNumber: string;
    floor: string;
}

/**
 * #18907: Dialog shown before creating a new valuation when the user's license already holds valuations
 * at the same address. Clicking a row opens that existing valuation (Ctrl/Cmd-click opens it in a new
 * browser tab via the `?search=` auto-input, #18908); the primary button creates a new valuation anyway,
 * the secondary one cancels (nothing is created; the user can adjust the input).
 */
export default function DuplicateValuationDialog({addressInputRef}: DuplicateValuationDialogProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    const duplicateData = useRevaSelector(selectDuplicateValuationsData);
    const isDarkTheme = useRevaSelector(selectDarkTheme);
    const lifeCycleColors = getLifeCycleStateColors(isDarkTheme);

    const visible = !!duplicateData;

    /** Localized segment label with a graceful fallback to the raw segment code for unknown segments. */
    const segmentLabel = (segment: string | null): string => {
        if (!segment) return "";
        return t(`importDialog.segment.${segment}`, segment);
    };

    const rows: DuplicateRow[] = useMemo(
        () =>
            (duplicateData?.matches ?? []).map((match) => ({
                revaId: match.revaId,
                openable: match.openable !== false,
                lifeCycleState: match.lifeCycleState,
                owner: match.ownerFullName || match.owner,
                segmentText: segmentLabel(match.segment),
                stateText: match.lifeCycleState ? t(lifeCycleStateLabelKey(match.lifeCycleState)) : "",
                editorsText: (match.editors ?? [])
                    .map((editor) => match.editorFullNames?.[editor] || editor)
                    .join(", "),
                ordersText: (match.externalOrderNumbers ?? []).join(", "),
                creationDate: match.creationDate ?? "",
                apartmentNumber: match.apartmentNumber ?? "",
                floor: match.floor ?? "",
            })),
        [duplicateData, language],
    );

    const onRowClick = (event: DataTableRowClickEvent) => {
        const row = event.data as DuplicateRow;
        if (!row?.revaId || !row.openable) return;
        const mouseEvent = event.originalEvent;
        if (mouseEvent && (mouseEvent.ctrlKey || mouseEvent.metaKey)) {
            // Ctrl/Cmd-click: open the valuation in a new browser tab. The root route keeps the query
            // string through its redirects, and the ?search= auto-input (#18908) then loads the REVA-ID.
            window.open(`/?search=${encodeURIComponent(row.revaId)}`, "_blank", "noopener");
            return;
        }
        dispatch(clearDuplicateValuations());
        void dispatch(fetchPersistence({id: row.revaId}));
    };

    const onCreateNew = () => {
        const pendingAddress = duplicateData?.pendingAddress;
        dispatch(clearDuplicateValuations());
        if (pendingAddress) {
            // The user has already seen the duplicates and chose to create anyway → skip the check.
            void dispatch(initializePersistence(pendingAddress, {skipDuplicateCheck: true}));
        }
    };

    const onCancel = () => {
        dispatch(clearDuplicateValuations());
        addressInputRef.current?.focus?.();
    };

    const lifeCycleStateTemplate = (row: DuplicateRow) =>
        row.lifeCycleState ? <ColorDotLabel color={lifeCycleColors[row.lifeCycleState]} label={row.stateText} /> : null;

    /** Segment label; locked rows additionally get a lock icon whose tooltip explains the missing permission. */
    const segmentTemplate = (row: DuplicateRow) => (
        <div className="flex items-center gap-2">
            {!row.openable && (
                <i
                    className={`${PrimeIcons.LOCK} duplicate-lock-icon text-surface-400`}
                    data-pr-tooltip={
                        row.owner
                            ? t("start.duplicateDialog.notOpenableOwner", {owner: row.owner})
                            : t("start.duplicateDialog.notOpenable")
                    }
                    data-pr-position="right"
                />
            )}
            <span>{row.segmentText}</span>
        </div>
    );

    const footer = (
        <div className="flex justify-end gap-2">
            <Button
                text
                icon={PrimeIcons.TIMES}
                label={t("start.duplicateDialog.cancel")}
                onClick={onCancel}
                data-testid="duplicate-dialog-cancel"
            />
            <Button
                icon={PrimeIcons.PLUS}
                label={t("start.duplicateDialog.createNew")}
                onClick={onCreateNew}
                data-testid="duplicate-dialog-create-new"
                // Focused on open (focusOnShow of the dialog is off) so plain Enter confirms
                // "create new anyway" while Escape still cancels via the dialog's onHide.
                autoFocus
            />
        </div>
    );

    return (
        <Dialog
            header={t("start.duplicateDialog.header")}
            visible={visible}
            modal={true}
            onHide={onCancel}
            style={{width: "90vw", maxWidth: "90rem"}}
            footer={footer}
            focusOnShow={false}
            pt={{root: {"data-testid": "duplicate-valuation-dialog"}}}
        >
            <Message severity="info" text={t("start.duplicateDialog.intro")} className="mb-4 w-full" />
            <Tooltip target=".duplicate-lock-icon" />
            <DataTable<DuplicateRow[]>
                size="small"
                value={rows}
                dataKey="revaId"
                selectionMode="single"
                onRowClick={onRowClick}
                rowClassName={(row: DuplicateRow) => (row.openable ? "" : "opacity-50")}
                sortField="creationDate"
                sortOrder={-1}
                removableSort
                stripedRows
                scrollable
                scrollHeight="50vh"
                pt={{root: {"data-testid": "duplicate-valuation-table"}}}
            >
                <Column
                    header={t("start.duplicateDialog.segment")}
                    field="segmentText"
                    sortable
                    body={segmentTemplate}
                />
                <Column header={t("start.duplicateDialog.apartmentNumber")} field="apartmentNumber" sortable />
                <Column header={t("start.duplicateDialog.floor")} field="floor" sortable />
                <Column
                    header={t("start.valuations.lifeCycleState")}
                    field="stateText"
                    sortable
                    body={lifeCycleStateTemplate}
                />
                <Column header={t("start.duplicateDialog.editors")} field="editorsText" sortable />
                <Column
                    header={t("start.valuations.createdDate")}
                    field="creationDate"
                    sortable
                    body={(row: DuplicateRow) => formatDateAndTime(row.creationDate, language)}
                />
                <Column header={t("start.valuations.externalOrderNumber")} field="ordersText" sortable />
                <Column header={t("start.valuations.id")} field="revaId" sortable bodyClassName="whitespace-nowrap" />
            </DataTable>
            <div className="text-surface-400 dark:text-surface-400 mt-2 text-sm">
                {t("start.duplicateDialog.newTabHint")}
            </div>
        </Dialog>
    );
}
